// Extraction quality — the bench and production in one place.
//
// Two questions that are only useful together:
//   BENCH       how good is extraction on documents we hold ground truth for
//   PRODUCTION  what is it actually doing to teachers' real uploads
// A bench score with no production runs is a lab result; production runs with
// no bench are anecdotes. The gold set is eight documents; teachers upload
// anything.
//
// What the columns are FOR, since a single number hides all of it:
//   - MEAN + RANGE, never one score. The same document, same config and same
//     code has scored 80.9% and 89.6% on consecutive runs. A change smaller
//     than a document's range is indistinguishable from luck.
//   - SYSTEMATIC vs INTERMITTENT failures. Only the systematic ones can be
//     fixed by a change; the rest are what a single run would misreport.
//   - Production rows show SIGNALS, not scores, because there is no truth to
//     score against: options that cannot be right, and questions two models
//     read differently.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    adminApi,
    type ExtractionEvalDetail,
    type ExtractionEvalLabel,
    type ExtractionQuestionScore,
    type QuestionExtractionRunView,
} from '../../../api/adminApi';
import { DataTable, Badge, LoadingSpinner, type ColumnDef } from '@shared-ui/components';
import './ExtractionTab.css';

const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
const inr = (usd: number) => `₹${(usd * 88).toFixed(1)}`;

/** Wide spread means the document itself is unstable, and a single run of it
 *  should not be quoted. 5 points is where that starts to matter in practice. */
const WIDE_RANGE = 0.05;

export function ExtractionTab() {
    const navigate = useNavigate();
    const [labels, setLabels] = useState<ExtractionEvalLabel[] | null>(null);
    const [runs, setRuns] = useState<QuestionExtractionRunView[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [openLabel, setOpenLabel] = useState<string | null>(null);
    /** Which document's per-question breakdown is expanded, within openLabel. */
    const [openDoc, setOpenDoc] = useState<string | null>(null);

    useEffect(() => {
        let live = true;
        Promise.all([
            adminApi.getExtractionEvals().catch(() => ({ labels: [] })),
            adminApi.getQuestionExtractionRuns({ limit: 50 }).catch(() => ({ items: [] })),
        ]).then(([e, p]) => {
            if (!live) return;
            setLabels(e.labels);
            setRuns(p.items);
        }).catch(err => live && setError(String(err)));
        return () => { live = false; };
    }, []);

    const benchColumns = useMemo<ColumnDef<ExtractionEvalLabel['documents'][number]>[]>(() => [
        { key: 'doc', header: 'Document', render: d => (
            <div>
                <div className="xt-doc">{d.documentId}</div>
                {d.documentName && <div className="xt-sub">{d.documentName}</div>}
            </div>
        ) },
        { key: 'model', header: 'Model', render: d => <span className="xt-chip">{d.provider ?? '—'}</span> },
        { key: 'runs', header: 'Runs', render: d => String(d.runs) },
        { key: 'score', header: 'Mean', render: d => <strong>{pct(d.meanScore)}</strong> },
        {
            // The range IS the finding on an unstable document — badge it so a
            // reader cannot quote the mean without seeing the spread.
            key: 'range', header: 'Range', render: d => {
                const spread = d.maxScore - d.minScore;
                const text = d.runs < 2 ? 'single run' : `${pct(d.minScore)}–${pct(d.maxScore)}`;
                if (d.runs < 2) return <Badge variant="warning" size="sm">{text}</Badge>;
                return spread >= WIDE_RANGE
                    ? <Badge variant="warning" size="sm">{text}</Badge>
                    : <span className="xt-sub">{text}</span>;
            },
        },
        { key: 'recall', header: 'Recall', render: d => (
            d.meanRecall < 1 ? <Badge variant="error" size="sm">{pct(d.meanRecall)}</Badge> : pct(d.meanRecall)
        ) },
        { key: 'sys', header: 'Always wrong', render: d => (
            d.systematic.length
                ? <span className="xt-fail" title={d.systematic.join(', ')}>{d.systematic.length}</span>
                : <span className="xt-ok">0</span>
        ) },
        { key: 'int', header: 'Intermittent', render: d => (
            d.intermittent.length ? <span className="xt-sub">{d.intermittent.length}</span> : <span className="xt-ok">0</span>
        ) },
        { key: 'cost', header: 'Cost', render: d => <span className="xt-sub">{inr(d.costUsd)}</span> },
    ], []);

    const prodColumns = useMemo<ColumnDef<QuestionExtractionRunView>[]>(() => [
        { key: 'when', header: 'When', render: r => new Date(r.startedAt).toLocaleString() },
        { key: 'source', header: 'Source', render: r => (
            <div>
                <div className="xt-doc">{r.sourceFileName || '—'}</div>
                <div className="xt-sub">{r.sourceKind}</div>
            </div>
        ) },
        { key: 'teacher', header: 'Teacher', render: r => <span className="xt-sub">{r.teacherName || r.teacherEmail || '—'}</span> },
        { key: 'q', header: 'Questions', render: r => String(r.extracted ?? '—') },
        {
            // Structurally impossible options — free to detect, and the only
            // signal here that needs no second model and no ground truth.
            key: 'defects', header: 'Impossible options', render: r => {
                const n = r.optionDefectCount;
                // Absent means the run predates the check — not that it was clean.
                if (n == null) return <span className="xt-sub" title="not checked">—</span>;
                return n > 0 ? <Badge variant="error" size="sm">{n}</Badge> : <span className="xt-ok">0</span>;
            },
        },
        {
            // Only present when the cross-check gate is on for that teacher.
            key: 'xcheck', header: 'Cross-check', render: r => {
                const d = r.crossCheckDisagreed;
                const p = r.crossCheckProvider;
                if (d == null) return <span className="xt-sub">off</span>;
                return (
                    <div>
                        {d > 0 ? <Badge variant="warning" size="sm">{d} differ</Badge> : <span className="xt-ok">agreed</span>}
                        {p && <div className="xt-sub">vs {p}</div>}
                    </div>
                );
            },
        },
        {
            // DOCX only. `equations` far above `equationsConverted` is the shape
            // that preceded a run "succeeding" with invented numbers: equations
            // the converter dropped never reached the model, which then filled
            // the gap. Blank for PDFs, where the counter has no meaning.
            key: 'eq', header: 'Equations', render: r => {
                if (r.sourceKind !== 'docx' || r.equations == null) return <span className="xt-sub">—</span>;
                const got = r.equationsConverted ?? 0;
                return got < r.equations
                    ? <Badge variant="error" size="sm">{got}/{r.equations}</Badge>
                    : <span className="xt-ok">{got}/{r.equations}</span>;
            },
        },
        {
            // Kept last and de-emphasised on purpose: the judge caught 8 of 28
            // real errors on a verified paper and three of its own corrections
            // were wrong. It is context, not a verdict.
            //
            // The count is shown WITH whether the judge was independent, because
            // the two cannot be read apart: a judge running the extraction's own
            // model is re-reading its own work, and the mistakes it is likeliest
            // to make are the ones it is least able to see. "0 flagged" from such
            // a judge is not evidence of a clean import.
            key: 'judge', header: 'Judge', render: r => {
                if (r.validationWrong == null) return <span className="xt-sub">—</span>;
                return (
                    <div>
                        <span className="xt-sub">{r.validationWrong} flagged</span>
                        {r.validationJudgeIndependent === false && (
                            <div>
                                <Badge variant="warning" size="sm" title={`judge and extraction both ran ${r.validationJudgeProvider}`}>
                                    same model
                                </Badge>
                            </div>
                        )}
                    </div>
                );
            },
        },
        // `costUsd` is optional even when `llm` is present — a run that recorded
        // tokens but no price is not free, so show nothing rather than ₹0.0.
        { key: 'cost', header: 'Cost', render: r => <span className="xt-sub">{r.llm?.costUsd != null ? inr(r.llm.costUsd) : '—'}</span> },
    ], []);

    if (error) return <div className="xt-error">Failed to load: {error}</div>;
    if (!labels || !runs) return <LoadingSpinner />;

    return (
        <div className="xt-page">
            <section>
                <h2>Bench — gold set</h2>
                <p className="xt-note">
                    Documents with hand-verified ground truth. <strong>Mean and range</strong> across repeats —
                    a change smaller than a document&apos;s range is indistinguishable from noise.
                    <strong> Always wrong</strong> is what a fix can target; intermittent failures are what a
                    single run would misreport. <strong>Click a document</strong> for the per-question
                    breakdown: which dimension missed, and what the model wrote where the page says
                    something else.
                </p>

                {labels.length === 0 && (
                    <div className="xt-empty">
                        No published eval runs yet. Publish one with{' '}
                        <code>npx tsx scripts/areas/extraction-eval/run-baseline.ts --label &lt;name&gt; --repeat 3 --publish</code>
                    </div>
                )}

                {labels.map(l => (
                    <div key={l.label} className="xt-label">
                        <button
                            className="xt-label-head"
                            onClick={() => {
                                setOpenLabel(openLabel === l.label ? null : l.label);
                                setOpenDoc(null);
                            }}
                        >
                            <span className="xt-label-name">{l.label}</span>
                            <span className="xt-label-macro">{pct(l.macroAverage)}</span>
                            <span className="xt-sub">
                                {l.documents.length} docs · {l.rubricVersion} · {inr(l.totalCostUsd)} ·{' '}
                                {new Date(l.createdAt).toLocaleDateString()}
                            </span>
                            <span className="xt-caret">{openLabel === l.label ? '▾' : '▸'}</span>
                        </button>
                        {openLabel === l.label && (
                            <>
                                <DataTable
                                    columns={benchColumns}
                                    data={l.documents}
                                    rowKey={d => d.documentId}
                                    onRowClick={d => setOpenDoc(openDoc === d.documentId ? null : d.documentId)}
                                />
                                {openDoc && <DocumentDetail label={l.label} documentId={openDoc} />}
                            </>
                        )}
                    </div>
                ))}
            </section>

            <CompareSection labels={labels} />

            <section>
                <h2>Production — teacher imports</h2>
                <p className="xt-note">
                    Real uploads. There is no ground truth here, so these are <strong>signals, not scores</strong>:
                    options that cannot be right, and questions a second model read differently.
                </p>
                <DataTable
                    columns={prodColumns}
                    data={runs}
                    rowKey={r => r.id}
                    onRowClick={r => navigate(`/operations/extraction/${r.id}`)}
                />
            </section>
        </div>
    );
}

/**
 * Same document, different labels, side by side — the comparison the per-label
 * tables cannot show.
 *
 * The rule this enforces so a reader cannot skip it: a lead is only called a
 * lead when it EXCEEDS the noise. Consecutive runs of one document under
 * identical code and config have differed by 8.7 points; a 3-point gap between
 * two models on that document means nothing. Every earlier "regression" found
 * in this work turned out to be a defect in the measurement, so the ranking is
 * withheld rather than shown with a caveat underneath it.
 *
 * Labels are only comparable within one rubric version. Scores from different
 * rubrics are different units, and putting them in one row would invent a
 * result — so older-rubric labels are excluded by name, not silently dropped.
 */
function CompareSection({ labels }: { labels: ExtractionEvalLabel[] }) {
    const { rubric, compared, excluded, rows } = useMemo(() => {
        const newest = labels[0]?.rubricVersion;
        const compared = labels.filter(l => l.rubricVersion === newest);
        const excluded = labels.filter(l => l.rubricVersion !== newest);

        const docIds = [...new Set(compared.flatMap(l => l.documents.map(d => d.documentId)))].sort();
        const rows = docIds.map(documentId => {
            const cells = compared.map(l => {
                const d = l.documents.find(x => x.documentId === documentId);
                return d && { label: l.label, mean: d.meanScore, spread: d.maxScore - d.minScore, runs: d.runs };
            });
            const present = cells.filter(Boolean) as NonNullable<(typeof cells)[number]>[];
            const ranked = [...present].sort((a, b) => b.mean - a.mean);
            // A single run has no measured spread, so fall back to the widest
            // spread seen on this document — never to zero, which would call
            // every difference significant.
            const fallback = Math.max(...present.map(p => p.spread), 0);
            const noise = (c: { spread: number; runs: number }) => (c.runs < 2 ? fallback : c.spread);
            const winner = ranked.length >= 2
                && ranked[0].mean - ranked[1].mean > Math.max(noise(ranked[0]), noise(ranked[1]))
                ? ranked[0].label
                : null;
            return { documentId, cells, winner, tied: ranked.length >= 2 && !winner };
        });
        return { rubric: newest, compared, excluded, rows };
    }, [labels]);

    if (compared.length < 2) return null;

    return (
        <section>
            <h2>Compare</h2>
            <p className="xt-note">
                Mean across repeats, with the spread beneath it. A label is only marked ahead when it
                beats the next one by <strong>more than the noise</strong> on that document — otherwise
                the row reads <em>too close to call</em>, because it is. Rubric <code>{rubric}</code>.
                {excluded.length > 0 && (
                    <> Excluded as scored under a different rubric: {excluded.map(l => l.label).join(', ')}.</>
                )}
            </p>
            <div className="xt-cmp-wrap">
                <table className="xt-cmp">
                    <thead>
                        <tr>
                            <th>Document</th>
                            {compared.map(l => <th key={l.label}>{l.label}</th>)}
                            <th>Verdict</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.documentId}>
                                <td className="xt-doc">{r.documentId}</td>
                                {r.cells.map((c, i) => (
                                    <td key={compared[i].label} className={c && r.winner === c.label ? 'xt-cmp-win' : ''}>
                                        {c ? (
                                            <>
                                                <div>{pct(c.mean)}</div>
                                                <div className="xt-sub">
                                                    {c.runs < 2 ? 'single run' : `±${(c.spread * 50).toFixed(1)}`}
                                                </div>
                                            </>
                                        ) : <span className="xt-sub">not run</span>}
                                    </td>
                                ))}
                                <td>
                                    {r.winner
                                        ? <Badge variant="success" size="sm">{r.winner}</Badge>
                                        : r.tied
                                            ? <span className="xt-sub">too close to call</span>
                                            : <span className="xt-sub">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

const DIMENSIONS = ['stem', 'mathTokens', 'options', 'correctAnswer', 'questionType'] as const;

/**
 * One gold document, every repeat, question by question.
 *
 * The score tables above say a question is wrong. This says what is wrong with
 * it: which dimension missed, and what the model wrote where the page says
 * something else. Nothing upstream of this view is enough to write a fix from.
 *
 * `mathTokens` is called out because it is the failure that ships a wrong
 * question to a student while every other dimension looks healthy — `a_{2n-1}`
 * read as `a_{2n-2}` is a near-perfect string match and a hard fail here.
 */
function DocumentDetail({ label, documentId }: { label: string; documentId: string }) {
    const [detail, setDetail] = useState<ExtractionEvalDetail | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let live = true;
        setDetail(null);
        setError(null);
        adminApi.getExtractionEvalDocument(label, documentId)
            .then(d => live && setDetail(d))
            .catch(e => live && setError(String(e)));
        return () => { live = false; };
    }, [label, documentId]);

    const rows = useMemo(() => {
        if (!detail) return [];
        const byQuestion = new Map<string, { perRun: (ExtractionQuestionScore | 'dropped' | undefined)[] }>();
        const ensure = (n: string) => {
            let e = byQuestion.get(n);
            if (!e) { e = { perRun: new Array(detail.runs.length).fill(undefined) }; byQuestion.set(n, e); }
            return e;
        };
        detail.runs.forEach((run, i) => {
            for (const q of run.perQuestion) ensure(q.questionNumber).perRun[i] = q;
            // A dropped question has no per-question score at all. It is the
            // worst outcome, so it must appear as a row rather than as a gap.
            for (const n of run.droppedQuestionNumbers) ensure(n).perRun[i] = 'dropped';
        });
        return [...byQuestion.entries()]
            .map(([questionNumber, v]) => {
                const scored = v.perRun.filter((x): x is ExtractionQuestionScore => !!x && x !== 'dropped');
                const worst = [...scored].sort((a, b) => a.weightedScore - b.weightedScore)[0];
                const failing = worst
                    ? DIMENSIONS.flatMap(dim => {
                        const f = worst[dim];
                        return f && f.score < 1 ? [{ dim, ...f }] : [];
                    })
                    : [];
                return {
                    questionNumber,
                    perRun: v.perRun,
                    dropped: v.perRun.some(x => x === 'dropped'),
                    clean: scored.length > 0 && scored.every(s => s.weightedScore >= 1),
                    failing,
                };
            })
            // Worst first: dropped, then anything failing, then clean.
            .sort((a, b) => Number(b.dropped) - Number(a.dropped)
                || Number(a.clean) - Number(b.clean)
                || a.questionNumber.localeCompare(b.questionNumber, undefined, { numeric: true }));
    }, [detail]);

    if (error) return <div className="xt-error">{error}</div>;
    if (!detail) return <LoadingSpinner />;

    return (
        <div className="xt-detail">
            <div className="xt-detail-head">
                {detail.runs.map(r => (
                    <span key={r.runIndex} className="xt-sub">
                        run {r.runIndex}: <strong>{pct(r.overallScore)}</strong>
                        {' '}(recall {pct(r.recall)}, precision {pct(r.precision)})
                        {r.droppedQuestionNumbers.length > 0 && (
                            <> · <span className="xt-fail">dropped {r.droppedQuestionNumbers.join(', ')}</span></>
                        )}
                        {r.swallowedNonQuestions.length > 0 && (
                            <> · swallowed {r.swallowedNonQuestions.length} non-question</>
                        )}
                    </span>
                ))}
            </div>
            <div className="xt-cmp-wrap">
                <table className="xt-cmp">
                    <thead>
                        <tr>
                            <th>Q</th>
                            {detail.runs.map(r => <th key={r.runIndex}>run {r.runIndex}</th>)}
                            <th>What is wrong — page vs model</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.questionNumber}>
                                <td><strong>{row.questionNumber}</strong></td>
                                {row.perRun.map((c, i) => (
                                    <td key={i}>
                                        {c === 'dropped'
                                            ? <span className="xt-fail">dropped</span>
                                            : c
                                                ? <span className={c.weightedScore < 1 ? 'xt-fail' : 'xt-ok'}>{pct(c.weightedScore)}</span>
                                                : <span className="xt-sub">—</span>}
                                    </td>
                                ))}
                                <td>
                                    {row.failing.length === 0
                                        ? <span className="xt-ok">clean</span>
                                        : row.failing.map(f => (
                                            <div key={f.dim} className="xt-dim">
                                                <span className="xt-chip">{f.dim}</span>{' '}
                                                <span className="xt-sub">{f.verdict} {pct(f.score)}</span>
                                                {f.expected != null && (
                                                    <div className="xt-diff">
                                                        <div><span className="xt-ok">page</span> <code>{f.expected}</code></div>
                                                        <div><span className="xt-fail">model</span> <code>{f.actual}</code></div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
